// Adapted from dev-scrips-pm-react src/scheduling/components/view-appointment-panel/docs-tab.tsx
// (PROD-1713 — appointment deep-view panel).
/**
 * ViewAppointmentDocsTab — Docs tab body for the PA appointment panel.
 *
 * PARTIAL PORT (documented divergence from PM — see PA_vs_PM_GAP_ANALYSIS.md
 * §2c). PM's Docs tab scopes the list to the appointment's ENCOUNTER
 * (auto-creating one on first "Add document") and offers a consent
 * fill-and-sign flow. Neither is portable to PA as-is:
 *
 *   - `useEncounterByAppointment` / `useCreateEncounter` in
 *     `@/scheduling/services` are BOUNDED STUBS in this repo (disabled
 *     query / throwing mutation — see their file headers, PROD-1190
 *     cross-module TODO). PA's real encounter lifecycle lives in the
 *     separate `src/encounter/` module (reached via `/encounter/:id`),
 *     not through these scheduling-services stand-ins. Wiring the PM
 *     encounter-scoping here would either silently no-op (query never
 *     fires) or throw on every "Add document" — a fake tab, not a real
 *     one.
 *   - There is no `consent` module in PA — `ConsentFillSignPopup` /
 *     `ConsentExternalUploadDialog` / `useConsentMarksForProvider` don't
 *     exist here.
 *
 * So this tab ships the PATIENT-WIDE document list + add-document flow only
 * (both fully real — `use-patient-documents.ts` / `NewFilePopup` are ported,
 * working PA modules), matching what PM's own file header notes the backend
 * already supports (`encounterId: null` → the patient's full document list,
 * dev-curl verified). Encounter-scoping + consent fill/sign are BLOCKED
 * pending the real encounter-module integration — flagged, not faked.
 */

import { useEffect, useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Banner, DocumentsSection } from '@scripsteam/scrips-signal-ds';
import { DocumentList } from '@/documents/components/document-list';
import { NewFilePopup } from '@/documents/components/new-file-popup';
import { DocumentsPager } from '@/documents/components/documents-pager';
import { usePatientDocuments } from '@/documents/services/use-patient-documents';
import type { AppointmentResponse } from '@/types/scheduling-api';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { Plus } from 'lucide-react';

const PAGE_SIZE = 20;

interface ViewAppointmentDocsTabProps {
  appointment: AppointmentResponse | null;
}

export function ViewAppointmentDocsTab({ appointment }: ViewAppointmentDocsTabProps) {
  const patientId = findPatientParticipant(appointment?.participant)?.patient?.id;
  const [addOpen, setAddOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Reset pager whenever the patient changes — never carry pagination state
  // across patients.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [patientId]);

  // Patient-wide list (no encounter scope — see file header). Backend
  // accepts `encounterId: null` and returns the patient's full document
  // history (dev-curl verified, PM PR #797 note).
  const documentsQuery = usePatientDocuments({
    patientId,
    encounterId: null,
    pageNumber: page,
    pageSize: PAGE_SIZE,
    enabled: !!patientId,
  });

  if (!patientId) {
    return (
      <div data-slot="appt-tab-page">
        <p className="text-sm text-text-muted">
          Couldn't identify the patient for this appointment.
        </p>
      </div>
    );
  }

  const documents = documentsQuery.data;
  const pageFull = (documents?.length ?? 0) === PAGE_SIZE;

  return (
    <div data-slot="appt-tab-page">
      {/* DS-consumption (2026-09-22): the head is the DS `DocumentsSection` (a deliberate, approved choice, guarded in
          docs-tab.test.tsx). Its look on this page (19 Oct: "titles not tinted, Add document not on two lines") comes from
          the `appt-tab-page` rules in showcase-bridge.css: the title in the section role and the plain ink, no tinted icon
          chip, the action never wrapping. DS request: DocumentsSection tokens for title and chip, `data-slot`s on both. */}
      <DocumentsSection
        title={`Documents ${documents ? documents.length : 0}`}
        icon="section-docs"
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1">
            <Plus className="size-4" aria-hidden />
            Add document
          </Button>
        }
      >
        {/* Export 21-10 (PaApptDocsTab): a failed read is the error Banner with Try again (the query's refetch). */}
        {documentsQuery.isError && !documentsQuery.isFetching ? (
          <Banner
            variant="error"
            title="Couldn't load documents"
            action={
              <Button type="button" size="sm" variant="secondary" onClick={() => void documentsQuery.refetch()}>
                Try again
              </Button>
            }
          >
            Refresh the list to try again.
          </Banner>
        ) : (
          <DocumentList
            documents={documents}
            isLoading={documentsQuery.isLoading}
            isError={documentsQuery.isError}
            emptyMessage="No documents for this patient yet."
          />
        )}
      </DocumentsSection>

      {!documentsQuery.isLoading && (page > 1 || (documents?.length ?? 0) > 0) ? (
        <DocumentsPager
          page={page}
          pageFull={pageFull}
          isFetching={documentsQuery.isFetching}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => p + 1)}
        />
      ) : null}

      <NewFilePopup
        open={addOpen}
        onOpenChange={setAddOpen}
        patientId={patientId}
        encounterId={null}
      />
    </div>
  );
}
